import { Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { getAsinLabelPrintersByWarehouse, getHangTagPrintersByWarehouse } from './PrinterConfig';
import bulkHelpers from '../../helpers/bulk';
import SalesOrderDisplay from './SalesOrderDisplay';
import SkuDisplay from './SkuDisplay';


const NiceLabels = (props) => {

    const [searchType, setSearchType] = useState("salesOrderNumber");
    const [searchText, setSearchText] = useState("");
    const [salesOrder, setSalesOrder] = useState({});
    const [item, setItem] = useState({});
    const [hangTagPrinters, setHangTagPrinters] = useState([]);
    const [asinLabelPrinters, setAsinLabelPrinters] = useState([]);
    const [displayPrinterDropdowns, setDisplayPrinterDropdowns] = useState(false);
    const [selectedHangTagPrinter, setSelectedHangTagPrinter] = useState("");
    const [selectedAsinLabelPrinter, setSelectedAsinLabelPrinter] = useState("");
 

    useEffect(() => {
        if (props.warehouse) {
            const hangTagPrinters = getHangTagPrintersByWarehouse(props.warehouse);
            setHangTagPrinters(hangTagPrinters);
            setSelectedHangTagPrinter(hangTagPrinters[0]);

            const asinLabelPrinters = getAsinLabelPrintersByWarehouse(props.warehouse);
            setAsinLabelPrinters(asinLabelPrinters);
            setSelectedAsinLabelPrinter(asinLabelPrinters[0]);
        }
    }, [props.warehouse]);

    const search = () => {
        if (!searchType) {
            props.toast.error("Please select a search type from the dropdown menu");
        } else if (!searchText.trim()) {
            props.toast.error("Please enter a valid SKU/sales order number");
        } else if (searchType === "salesOrderNumber") {
            getSalesOrder();
        } else if (searchType === "sku") {
            getHangTagBySku();
        }
    }

    const getSalesOrder = async () => {
        setDisplayPrinterDropdowns(false);
        setSalesOrder({});
        setItem({});
        props.setLoading(true);
        try {
            const trimmedSoNumber = searchText.trim();
            const retrievedSalesOrder = await bulkHelpers.getHangTagsBySalesOrderNumber(trimmedSoNumber, props.token);
            setSalesOrder({
                ...retrievedSalesOrder,
                salesOrderNumber: trimmedSoNumber
            });
            setDisplayPrinterDropdowns(true);
        } catch (error) {
            console.log(error);
            props.toast.error("Failed to retrieve sales order from IMS API. Please contact a developer");
        }
        props.setLoading(false);
    };

    const getHangTagBySku = async () => {
        setDisplayPrinterDropdowns(false);
        setSalesOrder({});
        setItem({});
        props.setLoading(true);
        try {
            const trimmedSku = searchText.trim();
            const hangTag = await bulkHelpers.getHangTagBySku(trimmedSku, props.token);
            setItem(hangTag);
            setDisplayPrinterDropdowns(true);
        } catch (error) {
            props.toast.error("Failed to retrieve hang tag information from IMS API. Please contact a developer");
            console.log(error);
        }
        props.setLoading(false);
    };

    const printSalesOrder = async (printOptions) => {
        for (let i = 0; i < salesOrder.items.length; i++) {
            if (salesOrder.items[i].qty === "" || isNaN(salesOrder.items[i].qty)) {
                props.toast.error(`Please enter a numeric item quantity for SKU ${salesOrder.items[i].sku}`);
                return;
            }
        };

        props.setLoading(true);

        // Don't print items that are missing hang tag data
        const itemsWithHangTagInfo = salesOrder.items.filter(item => item.upc);

        try {
            const printPayload = {
                printHangTags: printOptions.printHangTags,
                printAsinLabels: printOptions.printAsinLabels,
                printChewyLabels: printOptions.printChewyLabels,
                printWalmartLabels: printOptions.printWalmartLabels,
                hangTagPrinter: selectedHangTagPrinter,
                asinPrinter: selectedAsinLabelPrinter,
                salesOrderNumber: salesOrder.salesOrderNumber,
                company: salesOrder.company,
                warehouse: props.warehouse,
                items: itemsWithHangTagInfo
            };

            await bulkHelpers.printLabels(printPayload, props.token);
            props.toast.success("Print job sent to Loftware");
        } catch (error) {
            props.toast.error("Failed to send print job to Loftware. Please contact a developer");
            if (error.data) {
                console.log(error.data);
            } else {
                console.log(error);
            }
        }
        props.setLoading(false);
    };

    const printItem = async (printOptions) => {
        if (isNaN(printOptions.quantity)) {
            props.toast.error("Please enter a numeric item quantity");
            return;
        }

        props.setLoading(true);
        try {
            const printPayload = {
                printHangTags: printOptions.printHangTags,
                printAsinLabels: printOptions.printAsinLabels,
                printChewyLabels: printOptions.printChewyLabels,
                printWalmartLabels: printOptions.printWalmartLabels,
                hangTagPrinter: selectedHangTagPrinter,
                asinPrinter: selectedAsinLabelPrinter,
                warehouse: props.warehouse,
                items: [{
                    ...item,
                    qty: printOptions.quantity
                }]
            };

            await bulkHelpers.printLabels(printPayload, props.token);
            props.toast.success("Print job sent to Loftware");
        } catch (error) {
            props.toast.error("Failed to send print job to Loftware. Please contact a developer");
            if (error.data) {
                console.log(error.data);
            } else {
                console.log(error);
            }
        }
        props.setLoading(false);
    }

    const textFieldStyle = {
        display: "block",
        marginBottom: 1
    };


    return (
        <Box className={props.isMobile ? "mobile-top" : "desktop-top"} sx={{ display: 'flex', flexDirection: 'column' }}>
            <h2>NiceLabel</h2>
            <FormControl sx={{ marginBottom: 2 }}>
                <Select style={{ width: 225, marginRight: 7 }} value={searchType} onChange={(e) => setSearchType(e.target.value)}>
                    <MenuItem key={1} value={"salesOrderNumber"}>{"Sales order number"}</MenuItem>
                    <MenuItem key={2} value={"sku"}>{"SKU"}</MenuItem>
               </Select>
                <FormHelperText>Search type</FormHelperText>
            </FormControl>

            <TextField sx={textFieldStyle} label="Search value" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            <Button sx={{ maxWidth: 120, margin: 0.5, marginBottom: 10 }} onClick={search} variant="contained">Search</Button>
            {displayPrinterDropdowns && (
                <div style={{marginLeft: 14}}>
                    <FormControl sx={{ marginBottom: 2 }}>
                        <Select style={{ width: 300, marginRight: 7 }} value={selectedHangTagPrinter} onChange={(e) => setSelectedHangTagPrinter(e.target.value)}>
                            {hangTagPrinters.map((printerName, index) => {
                                return (<MenuItem key={index} value={printerName}>{printerName}</MenuItem>);
                            })}
                        </Select>
                        <FormHelperText>Hang tag printer</FormHelperText>
                    </FormControl>
                    <FormControl>
                        <Select style={{ width: 300 }} value={selectedAsinLabelPrinter} onChange={(e) => setSelectedAsinLabelPrinter(e.target.value)}>
                            {asinLabelPrinters.map((printerName, index) => {
                                return (<MenuItem key={index} value={printerName}>{printerName}</MenuItem>);
                            })}
                        </Select>
                        <FormHelperText>ASIN label printer</FormHelperText>
                    </FormControl>
                </div>
            )}
            {salesOrder.company && <SalesOrderDisplay salesOrder={salesOrder}
                                                                 setSalesOrder={setSalesOrder}
                                                                 printSalesOrder={printSalesOrder}
                                                                 hangTagPrinters={hangTagPrinters}
                                                                 asinLabelPrinters={asinLabelPrinters}
                                                                 toast={props.toast}/>}
            {item.sku && <SkuDisplay item={item} printItem={printItem} hangTagPrinters={hangTagPrinters} asinLabelPrinters={asinLabelPrinters} toast={props.toast}/>}
        </Box>
    );
};

export default NiceLabels;